import React, { useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './FAQ.module.css';
import moranAvatar from '../../images/מורן וינברגר תדמית 3.webp';

gsap.registerPlugin(ScrollTrigger);

const faqData = [
  {
    question: 'למה בכלל צריך מעצבת פנים?',
    answer: 'תכנון מעמיק ומקצועי מונע טעויות יקרות. כשעובדים עם מעצבת פנים, אתם יודעים כבר מההתחלה כמה הכל יעלה, מה זה ידרוש מכם, ואיך הדברים הולכים להיראות. זה לא רק על יופי - זה על תכנון שעובד, תקציב שנשמר, ולוח זמנים שמתקיים.',
  },
  {
    question: 'מה ההבדל בין מעצבת פנים למעצבת שהיא גם מנהלת פרויקטים?',
    answer: 'מעצבת פנים בהכרח היא לא מנהלת פרויקטים. אני מגיעה מעולם התקציבאות וניהול פרויקטים ברמת המאקרו - אני יודעת לשלב בין אינסטלציה, חשמל, יועצים, בנוי ועיצוב ולהוביל את הפרויקט להצלחה. אני לא באה רק כדי שהמקום ייראה יפה, אני באה לגרום לו להצליח מכל הבחינות.',
  },
  {
    question: 'כמה עולה עיצוב פנים?',
    answer: 'כל פרויקט הוא ייחודי ולכן גם העלות משתנה. בפגישת האפיון הראשונה אני מבינה את הצרכים, את היקף הפרויקט ואת התקציב שנוח לכם - ומשם בונה הצעה מותאמת אישית. מה שבטוח: אתם תדעו את העלות המדויקת לפני שמתחילים, בשקיפות מלאה.',
  },
  {
    question: 'מתי הזמן הנכון לפנות למעצבת?',
    answer: 'ככל שמוקדם יותר, כך טוב יותר. קניתם בית? תגיעו עם התכניות. מתכננים שיפוץ? לפחות 3 חודשים לפני. ככל שנתחיל מוקדם יותר, נוכל לתכנן טוב יותר, לחסוך זמן ולמנוע הפתעות לא נעימות בדרך.',
  },
  {
    question: 'איך נראה תהליך העבודה?',
    answer: 'התהליך מתחיל באפיון מעמיק - מבינים את הצרכים שלכם, את האופי, את התקציב. משם עוברים לתכנון, בחירת חומרים וצבעים, ומגיעים להדמיה תלת-ממדית שמראה בדיוק איך הכל ייראה - עוד לפני ששוברים קיר אחד. יש לכם את הזמן לשנות, לדייק, ולהרגיש בטוחים.',
  },
  {
    question: 'איך אני יודע שלא אחרוג מהתקציב?',
    answer: 'כל פרויקט מתוקצב בדיוק מראש. אני מגיעה מעולם ניהול תקציבים - שקיפות ואמינות הם הבסיס של כל פרויקט שאני מובילה. אתם יודעים בדיוק כמה כל דבר עולה, ואני דואגת שהכל נשמר במסגרת בלי הפתעות.',
  },
  {
    question: 'מטבח - חברת מטבחים או מעצבת פנים?',
    answer: 'אני מתכננת לכם את המטבח מאפס, בוחרת חומרים ורהיטים, מנהלת את המשא ומתן מול הספקים ודואגת שתדעו בדיוק מה אתם מקבלים - וכמובן במחיר נמוך יותר ממה שהייתם משלמים לבד.',
  },
  {
    question: 'את עובדת גם עם עסקים?',
    answer: 'בהחלט! אני עובדת עם פרטיים ומסחריים כאחד, בכל הארץ. בין אם זו דירה, משרד, מסעדה או חנות - הגישה שלי זהה: תכנון מדויק, ניהול מקצועי, ותוצאה שגורמת למקום להצליח.',
  },
];

function TypingIndicator() {
  return (
    <div className={styles.typingDots}>
      <span className={styles.dot} />
      <span className={styles.dot} />
      <span className={styles.dot} />
    </div>
  );
}

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [typingIndex, setTypingIndex] = useState(null);
  const typingTimeoutRef = useRef(null);
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const chatRef = useRef(null);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (headerRef.current) {
        gsap.fromTo(headerRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
            scrollTrigger: { trigger: sectionRef.current, start: 'top 75%', once: true },
          }
        );
      }
      if (chatRef.current) {
        const items = chatRef.current.querySelectorAll('[data-msg]');
        gsap.fromTo(items,
          { opacity: 0, y: 20 },
          {
            opacity: 1, y: 0, duration: 0.6, stagger: 0.06, ease: 'power3.out',
            scrollTrigger: { trigger: chatRef.current, start: 'top 80%', once: true },
          }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  const handleQuestionClick = useCallback((i) => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    if (openIndex === i) {
      setOpenIndex(null);
      setTypingIndex(null);
      return;
    }

    setOpenIndex(null);
    setTypingIndex(i);

    typingTimeoutRef.current = setTimeout(() => {
      setTypingIndex(null);
      setOpenIndex(i);
    }, 800);
  }, [openIndex]);

  const whatsappUrl = 'https://wa.me/972502177062?text=' +
    encodeURIComponent('היי מורן, הגעתי מהאתר שלך ואשמח שנדבר על הפרויקט שלי');

  return (
    <section ref={sectionRef} className={styles.section} id="שאלות-תשובות">
      <div className={styles.bgOrb1} />
      <div className={styles.bgOrb2} />

      <div className={styles.container}>
        <div ref={headerRef} className={styles.header} style={{ opacity: 0 }}>
          <h2 className={styles.title}>
            שאלות <span className={styles.titleLight}>ותשובות</span>
          </h2>
          <p className={styles.subtitle}>כל מה שרציתם לדעת - במקום אחד</p>
          <p className={styles.description}>לחצו על כל שאלה והתשובה תיפתח</p>
        </div>

        <div ref={chatRef} className={styles.chatFrame}>
          <div className={styles.messagesArea}>
            {faqData.map((faq, i) => (
              <div key={i} className={styles.messageGroup} data-msg>
                <div className={styles.questionRow}>
                  <button
                    className={`${styles.questionBubble} ${openIndex === i ? styles.questionBubbleActive : ''}`}
                    onClick={() => handleQuestionClick(i)}
                  >
                    {faq.question}
                  </button>
                </div>

                {typingIndex === i && (
                  <div className={styles.answerRow}>
                    <img src={moranAvatar} alt="" className={styles.moranAvatar} loading="lazy" decoding="async" />
                    <div className={styles.answerBubble}>
                      <TypingIndicator />
                    </div>
                  </div>
                )}

                {openIndex === i && (
                  <div className={`${styles.answerRow} ${styles.answerRowVisible}`}>
                    <img src={moranAvatar} alt="" className={styles.moranAvatar} loading="lazy" decoding="async" />
                    <div className={styles.answerBubble}>
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className={styles.ctaMessageGroup} data-msg>
              <div className={styles.answerRow}>
                <img src={moranAvatar} alt="" className={styles.moranAvatar} loading="lazy" decoding="async" />
                <div className={styles.ctaBubble}>
                  <p className={styles.ctaBubbleText}>עוד שאלות? אני תמיד פה</p>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.ctaButton}
                  >
                    <span>שלחו הודעה בוואטסאפ</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
